import { doc, getDoc, collection, query, where, getDocs, addDoc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Diagnostic function to check chat service status
const runChatDiagnostic = async () => {
   try {
      // Basic connectivity test - try to access Firestore
      const testQuery = query(collection(db, 'trips'), where('id', '==', 'test'));
      await getDocs(testQuery);

      return {
         success: true,
         message: 'Chat service diagnostic passed',
         details: { service: 'WebSocket', status: 'operational' },
      };
   } catch (error) {
      console.error('Chat diagnostic failed:', error);
      return {
         success: false,
         message: error instanceof Error ? error.message : 'Unknown error',
         error,
      };
   }
};

const chatService = {
   /**
    * Add a user to a trip chat channel when they accept an invitation
    * @param tripId The ID of the trip
    * @param userId The ID of the user to add
    */
   addUserToTripChat: async (tripId: string, userId: string) => {
      try {
         // First check if the trip exists
         const tripDoc = await getDoc(doc(db, 'trips', tripId));

         if (!tripDoc.exists()) {
            throw new Error('Trip not found');
         }

         // Add record in the chatMembers collection to track this connection
         await addDoc(collection(db, 'chatMembers'), {
            tripId,
            userId,
            joinedAt: serverTimestamp(),
         });

         return true;
      } catch (error) {
         console.error('Error adding user to trip chat:', error);
         return false;
      }
   },

   /**
    * Create or get a one-on-one chat between two users
    * @param currentUserId The current user's ID
    * @param otherUserId The other user's ID
    */
   getDirectChatId: async (currentUserId: string, otherUserId: string): Promise<string> => {
      // Create a sorted array of user IDs to ensure consistency
      const members = [currentUserId, otherUserId].sort();
      return `messaging_${members.join('_')}`;
   },

   /**
    * Check if a user can chat with another user (based on shared trips)
    * @param currentUserId The current user's ID
    * @param otherUserId The ID of the user to chat with
    */
   canChatWithUser: async (currentUserId: string, otherUserId: string): Promise<boolean> => {
      try {
         // Check if they share any trips where both are accepted members
         const tripsQuery = query(
            collection(db, 'trips'),
            where('members', 'array-contains', currentUserId)
         );

         const tripsSnapshot = await getDocs(tripsQuery);

         for (const tripDoc of tripsSnapshot.docs) {
            const tripData = tripDoc.data();

            // Check if the other user is also a member of this trip
            if (tripData.members && tripData.members.includes(otherUserId)) {
               return true;
            }

            // Check if other user is the trip owner
            if (tripData.createdBy === otherUserId) {
               return true;
            }
         }

         // Also check trips created by the current user
         const ownedTripsQuery = query(
            collection(db, 'trips'),
            where('createdBy', '==', currentUserId)
         );

         const ownedTripsSnapshot = await getDocs(ownedTripsQuery);

         for (const tripDoc of ownedTripsSnapshot.docs) {
            const tripData = tripDoc.data();

            // Check if the other user is a member of this trip
            if (tripData.members && tripData.members.includes(otherUserId)) {
               return true;
            }
         }

         // No shared trips found
         return false;
      } catch (error) {
         console.error('Error checking chat permissions:', error);
         return false;
      }
   },

   /**
    * Get all users that the current user can chat with
    * @param currentUserId The current user's ID
    */
   getChatEligibleUsers: async (currentUserId: string): Promise<string[]> => {
      try {
         const eligibleUsers = new Set<string>();

         // Get trips created by the user
         const ownedTripsQuery = query(
            collection(db, 'trips'),
            where('createdBy', '==', currentUserId)
         );

         const ownedTripsSnapshot = await getDocs(ownedTripsQuery);

         // Add all members from owned trips
         ownedTripsSnapshot.forEach((tripDoc) => {
            const tripData = tripDoc.data();
            if (tripData.members && Array.isArray(tripData.members)) {
               tripData.members.forEach((memberId: string) => {
                  if (memberId !== currentUserId) {
                     eligibleUsers.add(memberId);
                  }
               });
            }
         });

         // Get trips where the user is a member
         const memberTripsQuery = query(
            collection(db, 'trips'),
            where('members', 'array-contains', currentUserId)
         );

         const memberTripsSnapshot = await getDocs(memberTripsQuery);

         // Add trip creators and other members
         memberTripsSnapshot.forEach((tripDoc) => {
            const tripData = tripDoc.data();

            // Add trip creator
            if (tripData.createdBy && tripData.createdBy !== currentUserId) {
               eligibleUsers.add(tripData.createdBy);
            }

            // Add other members
            if (tripData.members && Array.isArray(tripData.members)) {
               tripData.members.forEach((memberId: string) => {
                  if (memberId !== currentUserId) {
                     eligibleUsers.add(memberId);
                  }
               });
            }
         });

         return Array.from(eligibleUsers);
      } catch (error) {
         console.error('Error getting chat eligible users:', error);
         return [];
      }
   },

   // Remove user from trip chat
   removeUserFromTripChat: async (tripId: string, userId: string) => {
      try {
         // Remove records from chatMembers
         const q = query(
            collection(db, 'chatMembers'),
            where('tripId', '==', tripId),
            where('userId', '==', userId)
         );

         const querySnapshot = await getDocs(q);

         if (querySnapshot.empty) {
            return true;
         }

         // Delete all matching records
         const deletePromises = querySnapshot.docs.map((docSnapshot) => {
            return deleteDoc(docSnapshot.ref);
         });

         await Promise.all(deletePromises);

         return true;
      } catch (error) {
         console.error('Error removing user from trip chat:', error);
         return false;
      }
   },

   // Get users in a trip chat
   getTripChatMembers: async (tripId: string) => {
      try {
         const q = query(collection(db, 'chatMembers'), where('tripId', '==', tripId));

         const querySnapshot = await getDocs(q);

         const members = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
               id: doc.id,
               userId: data.userId,
               joinedAt: data.joinedAt?.toDate() || new Date(),
            };
         });

         return members;
      } catch (error) {
         console.error('Error getting trip chat members:', error);
         return [];
      }
   },

   // Run chat diagnostic
   runDiagnostic: runChatDiagnostic,
};

export default chatService; 